// Trading games: short questions on real past charts (or on the desk's own
// rules and risk math), drawn 10 at a time from a pool of 20 per level.
//
// The rule every game follows: score the DECISION, not the luck. An answer
// may depend only on what the player is shown (the candles up to "now", the
// facts) or on the desk's rules and math — never on what price did next.
// What happened next is shown after answering, labelled "not scored".
import type { Candle } from "../chart/indicators";
import type { ChartSignal } from "../chart/chartSignals";

export type { Candle };

export type Level = "beginner" | "intermediate" | "expert";
export const LEVELS: Level[] = ["beginner", "intermediate", "expert"];

export type GameTf = "15m" | "1h" | "4h" | "1d";
export const TF_SEC: Record<GameTf, number> = { "15m": 900, "1h": 3600, "4h": 14400, "1d": 86400 };

/** Colour roles on the chart: green = buy/profit, red = sell/loss, lime = the brand highlight. */
export type Tone = "profit" | "loss" | "brand" | "warning" | "muted";

export interface ChartLine {
  price: number;
  /** Short axis label: "Entry", "Stop", "Level A". */
  label: string;
  tone: Tone;
  dashed?: boolean;
}

export interface ChartMark {
  /** Open time of the candle it points at (one of the chart's candles). */
  time: number;
  /** One or two characters is best: "A", "B", "?" */
  label: string;
  tone: Tone;
  position: "above" | "below";
}

export interface ChartZone {
  from: number;
  to: number;
  tone: Tone;
}

export interface ChartSpec {
  tf: GameTf;
  /** What the player sees: CLOSED candles, oldest first. The last one is "now". */
  candles: Candle[];
  /** The candles after "now": drawn only once answered, as "what happened next" (never scored). */
  ahead?: Candle[];
  /**
   * How many of the last candles fill the view at first (default 80). The
   * older ones are still there: they seed the EMAs and the player can scroll
   * back to them. Use more (or all) when the question is about a long stretch.
   */
  focus?: number;
  /** Engine EMAs drawn from the candles (20 = blue, 50 = violet, 200 = grey). */
  emas?: (20 | 50 | 200)[];
  /** Volume bars under the candles. */
  volume?: boolean;
  lines?: ChartLine[];
  marks?: ChartMark[];
  zones?: ChartZone[];
  /** Drawn only after answering: the answer shown on the chart. */
  answerLines?: ChartLine[];
  answerMarks?: ChartMark[];
  answerZones?: ChartZone[];
}

/** One scenario fact shown as a small tile above the question ("Account" · "$1,000"). */
export interface Fact {
  label: string;
  value: string;
}

export interface Option {
  id: string;
  label: string;
  /** Shown if the player picks this (wrong) option: why it's not the answer. */
  why?: string;
}

interface QuestionBase {
  /** The question in one or two short plain-English sentences. */
  prompt: string;
  facts?: Fact[];
  chart?: ChartSpec;
  /** Why the right answer is right, in plain words (shown after answering). */
  explain: string;
  /** What price did next, shown after answering under "not scored". Never part of the grading. */
  outcome?: string;
}

export interface ChoiceQuestion extends QuestionBase {
  kind: "choice";
  /** 2 to 5 options. */
  options: Option[];
  /** The id of the right option. */
  answer: string;
}

export type Unit = "$" | "%" | "R" | "coins" | "×" | "h" | "";

export interface NumberQuestion extends QuestionBase {
  kind: "number";
  answer: number;
  /** Accepted distance from `answer` (absolute, same unit). */
  tolerance: number;
  unit: Unit;
  /** Decimals to show the answer with. */
  decimals: number;
}

/** "Click the chart where …": right when the clicked price is inside `accept`. */
export interface PriceQuestion extends QuestionBase {
  kind: "price";
  chart: ChartSpec;
  /** The ideal price, drawn after answering. */
  answer: number;
  accept: { from: number; to: number };
}

export type Question = ChoiceQuestion | NumberQuestion | PriceQuestion;
export type QuestionKind = Question["kind"];

/** The chart data a game reads. */
export interface Needs {
  tf: GameTf;
  /** Closed candles up to "now" (30–1000). 260 or more for EMA 200. */
  history: number;
  /** Candles after "now", for the "what happened next" reveal (0–200). */
  ahead?: number;
  /** Also the 4h candles closed by the same moment (up to 300), for the 4h bias. */
  htf?: boolean;
}

export interface GameData {
  /** "SOLUSDT" */
  symbol: string;
  /** "SOL" */
  coin: string;
  tf: GameTf;
  /**
   * Closed candles, oldest first; the last is "now". For a signals game this
   * is the whole 1h signal window (with `history` candles before it), and the
   * game picks its own "now" with `cutAt`.
   */
  candles: Candle[];
  /** The candles after "now" (empty for a signals game: `cutAt` makes them). */
  ahead: Candle[];
  /** 4h candles closed by "now", when `needs.htf`. */
  htf?: Candle[];
  /**
   * The engine's 1h signals in the window, when `signals: true`. Pick and
   * grade only by what was known when a signal fired (signalTime, action,
   * entry, stopLoss, tp1, tp2, riskReward, archetype, trigger, thesis,
   * againstBtc) — `result`, `r`, `fillTime`, `tp1Time`, `exitTime` and
   * `exitPrice` are the future: use them only in `outcome`.
   */
  signals?: ChartSignal[];
}

/** Seeded random numbers: a game given the same data and seed makes the same question. */
export interface Rng {
  /** [0, 1) */
  next(): number;
  /** Integer in [min, max], both included. */
  int(min: number, max: number): number;
  float(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  shuffle<T>(items: readonly T[]): T[];
  chance(p: number): boolean;
}

export interface GameDef {
  /** Unique, kebab-case: "trend-or-range". */
  id: string;
  level: Level;
  /** Two to four words. */
  title: string;
  /** One plain sentence: the skill it trains. */
  skill: string;
  /** Chart data it reads; omit for rules and math games (they make their own numbers). */
  needs?: Needs;
  /** Reads the engine's 1h signals (slow to load; a session holds at most a few). Needs `needs.tf` "1h". */
  signals?: boolean;
  /**
   * One question. Pure: the same data and seed give the same question.
   * `data` is null when the game has no `needs`. Return null when this data
   * can't make a fair question (a too-close call, too few swings): the
   * session retries with another moment.
   */
  make(data: GameData | null, rng: Rng): Question | null;
}
